// 3. Fixed Login Component:
import React, { useEffect, useState } from 'react';
import {
  Card, CardBody, Col, Container, Input, Label, Row, Button,
  Form, FormFeedback, Spinner, Modal, ModalHeader, ModalBody, ModalFooter
} from 'reactstrap';
import ParticlesAuth from '../AuthenticationInner/ParticlesAuth';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { loginUser } from '../../slices/login/thunk';
import { ToastContainer } from 'react-toastify';
import CommonButton from '../../Components/Utils/CommonButton';
import { createZitadelAuth } from "@zitadel/react";

const Login = ({ userManager, authConfig }) => {
  const { id: emailParam } = useParams();
  const dispatch = useDispatch();
  const [userLogin, setUserLogin] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const navigate = useNavigate();
  const signIn = useSelector(state => state.SignIn.signin);
  
  const environment = localStorage.getItem("environment");
  const isSSO = JSON.parse(localStorage.getItem("isSSO") || "false");
  const redirectUrl = localStorage.getItem("redirect_url");
  const zitadel = createZitadelAuth(authConfig);

  useEffect(() => {
    if (emailParam) {
      setUserLogin(prev => ({ ...prev, email: emailParam }));
    }
  }, [emailParam]);

  const lastChanged = new Date(signIn?.lastPasswordChanged);
  const daysSince = Math.floor(
    (Date.now() - lastChanged.getTime()) / (1000 * 60 * 60 * 24)
  );
  const remainingDays = 90 - daysSince;
useEffect(() => {
  if (!emailParam) return;

  // Handle Keycloak environment
  if (environment?.toLowerCase() === "keycloak") {
    if (signIn?.lastPasswordChanged && daysSince !== null) {
      if (daysSince >= 83 && daysSince <= 89) {
        setShowPasswordModal(true);
        return;
      }
    }
    
    // Keycloak login is handled in signInUser thunk
    navigate("/signin");
    return;
  }

  if ((environment?.toLowerCase() === "azure" || environment?.toLowerCase() === "google") && isSSO) {
    if (!redirectUrl) {
      console.error("Redirect URL not found for Azure/Google SSO");
      setLoading(false);
      return;
    }

    // Check password expiry first if applicable
    if (signIn?.lastPasswordChanged && daysSince !== null) {
      if (daysSince >= 83 && daysSince <= 89) {
        setShowPasswordModal(true);
        return; 
      }
    }

    setLoading(true);
    window.location.href = redirectUrl;
    return;
  }
  
  if (environment === "Zitadel" && emailParam && userManager?.signinRedirect) {
    if (signIn?.lastPasswordChanged) {
      const lastChanged = new Date(signIn.lastPasswordChanged);
      const daysSince = Math.floor(
        (Date.now() - lastChanged.getTime()) / (1000 * 60 * 60 * 24)
      );
  
      if (daysSince >= 83 && daysSince <= 89) {
        setShowPasswordModal(true);
        return; // stop here, don’t auto login
      }
    }
    !isSSO ? handleAutoLogin() : handleLoginIsSSO();
  }
}, [environment, emailParam, userManager, signIn?.lastPasswordChanged, isSSO, redirectUrl]);
  
  const handleLoginIsSSO = () => {
    setLoading(true);
    zitadel.authorize();
  };

  const handleAutoLogin = async () => {
    setLoading(true);
    try {
      
      // Use the UserManager instance method
      await userManager.signinRedirect({
        extraQueryParams: {
          login_hint: emailParam,
          email_hint: emailParam,
          username: emailParam
        }
      });
    } catch (error) {
      console.error('Auto login error:', error);
      setLoading(false);
    }
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: userLogin.email,
      password: userLogin.password,
    },
    validationSchema: Yup.object({
      email: Yup.string().required('Please Enter Your User ID'),
      password: Yup.string().required('Please Enter Your Password'),
    }),
    onSubmit: async (values) => {
      dispatch(loginUser(values, navigate, setLoading));
    },
  });

  const handleLogin = async () => {
    setLoading(true);
    
    if (!userManager || typeof userManager.signinRedirect !== 'function') {
      console.error('UserManager not properly initialized or missing signinRedirect method');
      setLoading(false);
      return;
    }

    try {
      
      const signinOptions = emailParam ? {
        extraQueryParams: {
          login_hint: emailParam,
          email_hint: emailParam,
          username: emailParam
        }
      } : {};
      
      await userManager.signinRedirect(signinOptions);
    } catch (error) {
      console.error('Login error:', error);
      setLoading(false);
    }
  };
  const handleContinue = () => {
    setShowPasswordModal(false);
    
    if ((environment === "Azure" || environment === "Google") && isSSO && redirectUrl) {
      setTimeout(() => {
        setLoading(true);
        window.location.href = redirectUrl;
      }, 100);
    } else {
      setTimeout(() => {
        handleAutoLogin();
      }, 100);
    }
  };
  document.title = 'K2charge';

  return (
    <ParticlesAuth>
      <div className='auth-page-content'>
        <Container>
          <ToastContainer position='top-center' />
          <Row className='justify-content-center'>
            <Col md={8} lg={6} xl={5}>
              <Card className='login-box'>
                <CardBody className='p-4'>
                  <div className='text-center mt-2'>
                    <h5 className='text-main-color'>Welcome Back !</h5>
                    <p className='text-muted'>Sign in to continue</p>
                  </div>
                  <div className='p-2 mt-4'>
                    {environment?.toLowerCase() === "native" ? (
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          validation.handleSubmit();
                          return false;
                        }}
                        action='#'
                      >
                        <div className='mb-3'>
                          <Label htmlFor='email' className='form-label'>
                            User ID
                          </Label>
                          <Input
                            name='email'
                            className='form-control'
                            placeholder='Enter User ID'
                            type='text'
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.email || ''}
                            disabled={true}
                            invalid={
                              validation.touched.email && validation.errors.email
                                ? true
                                : false
                            }
                          />
                          {validation.touched.email && validation.errors.email ? (
                            <FormFeedback type='invalid'>
                              {validation.errors.email}
                            </FormFeedback>
                          ) : null}
                        </div>
                        <div className='mb-3'>
                          <Label className='form-label' htmlFor='password-input'>
                            Password
                          </Label>
                          <div className='position-relative auth-pass-inputgroup mb-3'>
                            <Input
                              name='password'
                              value={validation.values.password || ''}
                              type={showPassword ? 'text' : 'password'}
                              className='form-control pe-5'
                              placeholder='Enter Password'
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={
                                validation.touched.password && validation.errors.password
                                  ? true
                                  : false
                              }
                            />
                            {validation.touched.password && validation.errors.password ? (
                              <FormFeedback type='invalid'>
                                {validation.errors.password}
                              </FormFeedback>
                            ) : null}
                            <button
                              className='btn btn-link position-absolute end-0 top-0'
                              onClick={() => setShowPassword(!showPassword)}
                              type='button'
                            >
                              <i className='ri-eye-fill align-middle'></i>
                            </button>
                          </div>
                        </div>
                        <div className='mt-1'>
                          <a href="/forgot-password" className='text-main-color text-decoration-underline'>Forgot Password?</a>
                          <Button className='btn bg-secondary w-100 mt-2' type='submit' disabled={loading}>
                            {loading && <Spinner size='sm' className='me-2'>Loading...</Spinner>}
                            Sign In
                          </Button>
                        </div>
                      </Form>
                    ) : (
                      <Form>
                        <div className='mt-1'>
                          <Button
                            className='btn bg-secondary w-100 mt-2'
                            type='button'
                            // onClick={handleLogin} 
                            disabled={loading}
                          >
                            {loading && <Spinner size='sm' className='me-2'>Loading...</Spinner>}
                            Sign In
                          </Button>
                        </div>
                      </Form>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Password Expiry Modal */}
          <Modal
            isOpen={showPasswordModal}
            toggle={() => setShowPasswordModal(false)}
            centered
            size="md"
            backdrop={"false"}
          >
            <ModalHeader className="d-flex justify-content-center align-items-center p-3">
              <div className="w-100 text-center fw-bold">Password about to Expire</div>
            </ModalHeader>
            <ModalBody>
              <p className="mb-4 w-100 text-center fw-bold fs-6">
                Your password is about to expire in {remainingDays} days, please reset the password!
              </p>
            </ModalBody>
            <ModalFooter className="d-flex justify-content-center border-0">
              <CommonButton
                color="secondary"
                type="button"
                buttonAction={handleContinue}
                buttonText="Continue"
                icon={<i className="ri-checkbox-circle-line align-middle fs-16 me-1"></i>}
                className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-md-auto text-nowrap"
                btnTxtClsName="px-2 ps-3 ms-2 me-1"
              />
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </ParticlesAuth>
  );
};

export default Login;